import { odataFetch } from "@/lib/odata-fetch";
import {
  qWorkOOperationDependency,
  QWorkOOperationDependency,
} from "@/odata/appu4dm/QAppU4DM";
import { createQueryBuilderV4 } from "@odata2ts/odata-query-builder";
import { useQuery } from "@tanstack/react-query";
import { operationDepsKeys } from "./operation-deps-keys";
import { WorkOOperationDependency } from "@/odata/appu4dm/AppU4DMModel";
import { useWorkOrder } from "../work-order/use-work-order";

export function useOperationDeps() {
  const { workOrder } = useWorkOrder();
  const query = useQuery({
    queryKey: operationDepsKeys.get(workOrder?.Id!),
    queryFn: ({ meta }) => fetchOperationDeps(workOrder?.Id!, meta?.token!),
    enabled: ({ meta }) => !!(meta?.token && workOrder?.Id),
  });

  return {
    ...query,
    operationDeps: query.data?.value ?? [],
  };
}

export type WorkOrderStatusNId = "Active" | "Partial" | "Open";
export type WorkOrderOperationDependency = Pick<
  WorkOOperationDependency,
  | "Id"
  | "FromWOO_Id"
  | "ToWOO_Id"
  | "PartialCompleted"
  | "DependencyType"
  | "FromWOO"
  | "ToWOO"
  | "WOOpDependencyNavigations"
>;

function fetchOperationDeps(workOrderId: string, token: string) {
  const builder = createQueryBuilderV4(
    "WorkOOperationDependency",
    new QWorkOOperationDependency()
  )
    .filter(
      qWorkOOperationDependency.FromWOO.props.WorkOrder_Id.eq(workOrderId)
    )
    .select(
      "Id",
      "FromWOO_Id",
      "ToWOO_Id",
      "PartialCompleted",
      "DependencyType"
    )
    .expanding("FromWOO", (q) =>
      q
        .select(
          "Id",
          "Name",
          "Description",
          "Sequence",
          "WorkOrder_Id",
          "Status",
          "ActiveNonConformanceNr",
          "PreviousStatus",
          "ProducedQuantity"
        )
        .expanding("WorkOrder", (q) => q.select("NId"))
    )
    .expanding("ToWOO", (q) =>
      q
        .select(
          "Id",
          "Name",
          "Description",
          "Sequence",
          "WorkOrder_Id",
          "Status",
          "ActiveNonConformanceNr"
        )
        .expanding("WorkOrder", (q) => q.select("NId"))
    )
    .expanding("WOOpDependencyNavigations", (q) =>
      q.select("AlternativeGroup", "IsPreferred")
    );

  return odataFetch<WorkOrderOperationDependency>({ builder, token });
}
